import React, { useState, useEffect, useContext, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Headphones, Mic, BookOpen, PenTool, ArrowLeft, Star, Printer,
  TrendingUp, Home, Heart, Loader2, ChevronRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import AuthContext from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOD_ICONS = { listening: Headphones, speaking: Mic, reading: BookOpen, writing: PenTool };
const MOD_COLORS = {
  listening: { main: "#4f46e5", light: "#eef2ff", border: "#c7d2fe" },
  speaking: { main: "#0284c7", light: "#f0f9ff", border: "#bae6fd" },
  reading: { main: "#16a34a", light: "#f0fdf4", border: "#bbf7d0" },
  writing: { main: "#d97706", light: "#fffbeb", border: "#fde68a" },
};

const StarRating = ({ count, max = 6, size = 16 }) => (
  <div className="flex gap-0.5" data-testid="star-rating">
    {Array.from({ length: max }).map((_, i) => (
      <Star
        key={i}
        size={size}
        className={i < count ? "text-yellow-400 fill-yellow-400" : "text-gray-200"}
      />
    ))}
  </div>
);

const LevelBar = ({ level, max = 6, color }) => (
  <div className="flex gap-1 items-center" data-testid="level-bar">
    <div className="flex-1 bg-gray-100 h-3 rounded-full overflow-hidden">
      <div
        className="h-full rounded-full transition-all duration-700"
        style={{ width: `${(level / max) * 100}%`, backgroundColor: color }}
      />
    </div>
    <span className="text-sm font-bold w-6 text-right" style={{ color }}>{level}</span>
    <span className="text-xs text-gray-400">/ {max}</span>
  </div>
);

const ParentReport = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [teacherNote, setTeacherNote] = useState("");
  const printRef = useRef();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("session_token");
        const res = await axios.get(`${API}/reports/parent-report/${studentId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data);
      } catch (err) {
        toast.error("Failed to load parent report");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [studentId]);

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="animate-spin text-[#002FA7]" size={32} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-12 text-center">
        <p className="text-gray-500">No data available.</p>
        <Button onClick={() => navigate(-1)} variant="outline" className="mt-4">
          <ArrowLeft size={16} className="mr-2" /> Go Back
        </Button>
      </div>
    );
  }

  const { student, report_period, overall, skills, strengths, areas_for_growth, home_activities } = data;

  return (
    <>
      {/* Print Styles */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .print-area, .print-area * { visibility: visible; }
          .print-area { position: absolute; left: 0; top: 0; width: 100%; }
          .no-print { display: none !important; }
          .print-area { padding: 0.5in; font-size: 11pt; }
          .page-break { page-break-before: always; }
        }
      `}</style>

      {/* Action Bar (hidden in print) */}
      <div className="no-print max-w-4xl mx-auto px-4 sm:px-6 pt-6 flex items-center gap-3">
        <Button onClick={() => navigate(-1)} variant="outline" size="sm" data-testid="back-btn">
          <ArrowLeft size={16} />
        </Button>
        <div className="flex-1">
          <span className="text-sm text-gray-500">Parent Report for {student.name}</span>
        </div>
        <Button onClick={handlePrint} className="bg-[#002FA7] hover:bg-[#002FA7]/90 text-white" data-testid="print-btn">
          <Printer size={16} className="mr-2" /> Print Report
        </Button>
      </div>

      {/* Printable Report Area */}
      <div ref={printRef} className="print-area max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="parent-report">
        {/* Header */}
        <div className="border-b-4 border-[#002FA7] pb-6 mb-8" data-testid="report-header">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#002FA7] font-bold mb-1">
                MOSAIC Student Progress Report
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {student.name}
              </h1>
              <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                <span>{student.grade_level || "Grade N/A"}</span>
                <span className="text-gray-300">|</span>
                <span>{report_period.total_assessments} assessment{report_period.total_assessments !== 1 ? "s" : ""}</span>
                {report_period.start && (
                  <>
                    <span className="text-gray-300">|</span>
                    <span>{report_period.start} to {report_period.end}</span>
                  </>
                )}
              </div>
            </div>
            <div className="text-right hidden sm:block">
              <div className="text-xs text-gray-400">Generated</div>
              <div className="text-sm text-gray-500">{new Date().toLocaleDateString()}</div>
            </div>
          </div>
        </div>

        {/* Overall Summary */}
        <div className="bg-[#002FA7]/5 border-2 border-[#002FA7]/20 p-6 mb-8" data-testid="overall-summary">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 rounded-full bg-[#002FA7] flex items-center justify-center">
                <span className="text-2xl font-black text-white">L{overall.level}</span>
              </div>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Overall: {overall.label}
                </h2>
                <StarRating count={overall.stars} />
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">{overall.description}</p>
            </div>
          </div>
        </div>

        {/* Skills Breakdown */}
        <h2 className="text-lg font-bold text-gray-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
          English Language Skills
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
          {["listening", "speaking", "reading", "writing"].map(mod => {
            const skill = skills[mod];
            if (!skill) return null;
            const Icon = MOD_ICONS[mod];
            const colors = MOD_COLORS[mod];

            return (
              <div
                key={mod}
                className="border-2 p-5"
                style={{ borderColor: colors.border, backgroundColor: colors.light }}
                data-testid={`skill-card-${mod}`}
              >
                {/* Skill Header */}
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: colors.main, color: 'white' }}>
                    <Icon size={16} />
                  </div>
                  <div className="flex-1">
                    <div className="font-bold text-sm" style={{ color: colors.main }}>{skill.name}</div>
                  </div>
                  <StarRating count={skill.stars} size={14} />
                </div>

                {/* Level + Description */}
                <div className="mb-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg font-black" style={{ color: colors.main }}>{skill.level_label}</span>
                    <span className="text-xs text-gray-400">Level {skill.current_level}/6</span>
                  </div>
                  <LevelBar level={skill.current_level} color={colors.main} />
                </div>

                <p className="text-sm text-gray-600 mb-2 leading-relaxed">{skill.level_description}</p>

                {/* Growth indicator */}
                {skill.growth_text && (
                  <div className="flex items-center gap-1.5 text-xs font-semibold mt-2 pt-2 border-t" style={{ borderColor: colors.border }}>
                    <TrendingUp size={12} style={{ color: colors.main }} />
                    <span style={{ color: colors.main }}>{skill.growth_text}</span>
                  </div>
                )}

                {/* Dimensional breakdown */}
                {Object.keys(skill.dimensions).length > 0 && (
                  <div className="mt-3 pt-2 border-t space-y-1.5" style={{ borderColor: colors.border }}>
                    {Object.entries(skill.dimensions).map(([dimKey, dim]) => (
                      <div key={dimKey} className="flex items-center gap-2 text-xs">
                        <span className="text-gray-500 w-28 truncate">{dim.name}</span>
                        <div className="flex-1 bg-white/60 h-2 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{ width: `${(dim.current / 6) * 100}%`, backgroundColor: colors.main, opacity: 0.7 }}
                          />
                        </div>
                        <span className="font-bold w-5 text-right" style={{ color: colors.main }}>{dim.current}</span>
                        {dim.growing === true && <TrendingUp size={10} className="text-green-500" />}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Strengths & Areas for Growth */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
          {/* Strengths */}
          <div className="border-2 border-green-200 bg-green-50 p-5" data-testid="strengths-section">
            <div className="flex items-center gap-2 mb-3">
              <Star size={18} className="text-green-600 fill-green-600" />
              <h3 className="font-bold text-green-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Strengths
              </h3>
            </div>
            {strengths.length > 0 ? (
              <ul className="space-y-2">
                {strengths.map((s, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-green-700">
                    <ChevronRight size={14} className="text-green-500 flex-shrink-0" />
                    {s}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-green-600">Keep encouraging your child — every step matters!</p>
            )}
          </div>

          {/* Areas for Growth */}
          <div className="border-2 border-amber-200 bg-amber-50 p-5" data-testid="growth-areas-section">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp size={18} className="text-amber-600" />
              <h3 className="font-bold text-amber-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Areas for Growth
              </h3>
            </div>
            {areas_for_growth.length > 0 ? (
              <ul className="space-y-2">
                {areas_for_growth.map((a, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-amber-700">
                    <ChevronRight size={14} className="text-amber-500 flex-shrink-0" />
                    {a}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-amber-600">Your child is making progress across all areas!</p>
            )}
          </div>
        </div>

        {/* What You Can Do At Home */}
        {home_activities.length > 0 && (
          <div className="border-2 border-blue-200 bg-blue-50 p-5 mb-8" data-testid="home-activities">
            <div className="flex items-center gap-2 mb-3">
              <Home size={18} className="text-blue-600" />
              <h3 className="font-bold text-blue-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
                What You Can Do At Home
              </h3>
            </div>
            <ul className="space-y-2">
              {home_activities.map((activity, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-blue-700">
                  <Heart size={12} className="text-blue-400 mt-0.5 flex-shrink-0" />
                  {activity}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Teacher Note */}
        <div className="border-2 border-gray-200 p-5 mb-8 no-print" data-testid="teacher-note-section">
          <h3 className="font-bold text-gray-700 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Teacher's Note (optional — will appear on printed report)
          </h3>
          <Textarea
            placeholder="Add a personal note for the parent about their child's progress..."
            value={teacherNote}
            onChange={(e) => setTeacherNote(e.target.value)}
            className="min-h-[80px] border-gray-300 text-sm"
            data-testid="teacher-note-input"
          />
        </div>

        {/* Teacher Note (print version) */}
        {teacherNote && (
          <div className="border-2 border-gray-200 p-5 mb-8 hidden print:block">
            <h3 className="font-bold text-gray-700 mb-2">Teacher's Note</h3>
            <p className="text-sm text-gray-600 whitespace-pre-wrap">{teacherNote}</p>
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-xs text-gray-400 pt-4 border-t border-gray-200" data-testid="report-footer">
          <p>MOSAIC — Multilingual Online & Scripted Assessment for Inclusive Classrooms</p>
          <p className="mt-0.5">MOSAIC results are practice and instructional indicators. They are not official WIDA ACCESS scores and should not be used alone for student identification, placement, reclassification, exit, or other high-stakes decisions. Consider required state assessments, classroom evidence, educator observations, and other relevant information.</p>
          <p className="mt-0.5">
            Questions? Contact your child's teacher or visit your school's parent portal.
          </p>
        </div>
      </div>
    </>
  );
};

export default ParentReport;
